import { DataTable } from '@/components/table/DataTable';
import { Box } from '@/components/ui/box';
import { Button } from '@/components/ui/button';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Text } from '@/components/ui/text';
import { MarkdownText } from '@/shared/MarkdownText';
import { UITools } from '@ditto/shared';
import { ColumnDef, getPaginationRowModel } from '@tanstack/react-table';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { FC, useState } from 'react';
import { Link } from 'react-router';
import { generateRegulationLink } from './utils';

export type Fda510kSubmissionDbSearchToolPart =
  UITools['fda510kSubmissionDbSearch'];

export type Fda510kSubmissionDbSearchToolInput =
  UITools['fda510kSubmissionDbSearch']['input'];

export type Fda510kSubmissionDbSearchToolOutput =
  UITools['fda510kSubmissionDbSearch']['output'];

export const Fda510kSubmissionDbSearchToolMessage: FC<{
  input?: Fda510kSubmissionDbSearchToolInput;
  output?: Fda510kSubmissionDbSearchToolOutput;
}> = ({ input, output }) => {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const markdownText = [
    input?.productCode ? `- Product Code: ${input.productCode}` : null,
    input?.device510kNumber
      ? `- Device 510(k) Number: ${input.device510kNumber}`
      : null,
    input?.deviceName ? `- Device Name: ${input.deviceName}` : null,
    input?.applicantName ? `- Applicant Name: ${input.applicantName}` : null,
    input?.limit && input.search_type === 'records'
      ? `- Limit: ${input.limit}`
      : null,
  ]
    .filter(Boolean)
    .join('\n');

  const searchUrl = `https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm?start_search=1&ProductCode=${input?.productCode ?? ''}&KNumber=${input?.device510kNumber ?? ''}`;

  const isCountSearch = input?.search_type === 'count';
  const hasResults = isCountSearch
    ? output?.count_results && output.count_results.length > 0
    : output?.submissions && output.submissions.length > 0;

  return (
    <FlexRow alignItems="stretch" w="full" gap="4">
      <FlexCol gap="4">
        <FlexCol gap="2">
          <a href={searchUrl} target="_blank" rel="noopener noreferrer">
            <FlexRow gap="1">
              <Text variant="body" className="text-neutral-600">
                Search in
              </Text>
              <Text variant="body" className="text-neutral-600 underline">
                FDA 510(k) Submission Database
              </Text>
              <Text variant="body" className="text-neutral-600">
                for:
              </Text>
            </FlexRow>
          </a>
          <MarkdownText>{markdownText}</MarkdownText>

          {/* Show metadata */}
          {output?.meta && (
            <FlexRow gap="2" className="text-sm text-neutral-500">
              <Text variant="body-sm">
                Total found: {output.meta.total?.toLocaleString() || 0}
              </Text>
              {output.meta.limit && (
                <Text variant="body-sm">
                  • Showing:{' '}
                  {Math.min(output.meta.limit, output.meta.total || 0)}
                </Text>
              )}
            </FlexRow>
          )}
        </FlexCol>

        {hasResults ? (
          <div className="overflow-y-auto max-w-[800px]">
            {isCountSearch ? (
              <DataTable
                columns={countColumns}
                data={output?.count_results!}
                options={{
                  getPaginationRowModel: getPaginationRowModel(),
                  onPaginationChange: setPagination,
                  state: { pagination },
                }}
              />
            ) : (
              <DataTable
                columns={columns}
                data={output?.submissions!}
                getRowCanExpand={() => true}
                renderSubComponent={submission => (
                  <AdditionalDetails submission={submission.original} />
                )}
                options={{
                  getPaginationRowModel: getPaginationRowModel(),
                  onPaginationChange: setPagination,
                  state: {
                    pagination,
                  },
                }}
              />
            )}
          </div>
        ) : (
          <FlexCol gap="2">
            <Text variant="body" className="text-neutral-600">
              No submissions found.
            </Text>
          </FlexCol>
        )}
      </FlexCol>
    </FlexRow>
  );
};

// Columns for count results
const countColumns: ColumnDef<{ term: string; count: number }>[] = [
  {
    accessorKey: 'term',
    header: () => (
      <Text variant="body" color="primary">
        Term
      </Text>
    ),
    cell: ({ row }) => (
      <Text variant="body" className="text-neutral-600">
        {row.original.term}
      </Text>
    ),
    meta: {
      cellClassName: 'bg-white border-b border-neutral-200 min-w-[200px]',
      headerClassName:
        'bg-neutral-100 border-b border-neutral-200 min-w-[200px]',
    },
  },
  {
    accessorKey: 'count',
    header: () => (
      <Text variant="body" color="primary">
        Count
      </Text>
    ),
    cell: ({ row }) => (
      <Text variant="body" className="text-neutral-600 font-mono">
        {row.original.count.toLocaleString()}
      </Text>
    ),
    meta: {
      cellClassName: 'bg-white border-b border-neutral-200 min-w-[100px]',
      headerClassName:
        'bg-neutral-100 border-b border-neutral-200 min-w-[100px]',
    },
  },
];

const columns: ColumnDef<
  NonNullable<Fda510kSubmissionDbSearchToolOutput['submissions']>[number]
>[] = [
  {
    id: 'expander',
    header: () => null,
    cell: ({ row }) => {
      return (
        <Button
          variant="ghost"
          size="sm"
          onClick={e => {
            e.stopPropagation();
            row.toggleExpanded();
          }}
          className="p-0 h-8 w-8"
        >
          {row.getIsExpanded() ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </Button>
      );
    },
    meta: {
      cellClassName: 'bg-white border-b border-neutral-200 min-w-[20px]',
      headerClassName:
        'bg-neutral-100 border-b border-neutral-200 min-w-[20px]',
    },
  },
  {
    accessorKey: 'deviceName',
    header: () => (
      <Text variant="body" color="primary">
        Device Name
      </Text>
    ),
    cell: ({ row }) => {
      return (
        <Link
          to={row.original.deviceNameLink ?? ''}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Text variant="body" className="text-neutral-600 hover:underline">
            {row.original.deviceName}
          </Text>
        </Link>
      );
    },
    meta: {
      cellClassName: 'bg-white border-b border-neutral-200 min-w-[110px]',
      headerClassName:
        'bg-neutral-100 border-b border-neutral-200 min-w-[110px]',
    },
  },
  {
    accessorKey: 'applicantName',
    header: () => (
      <Text variant="body" color="primary">
        Applicant Name
      </Text>
    ),
    cell: ({ row }) => {
      return (
        <Text variant="body" className="text-neutral-600">
          {row.original.applicantName}
        </Text>
      );
    },
    meta: {
      cellClassName: 'bg-white border-b border-neutral-200 min-w-[110px]',
      headerClassName:
        'bg-neutral-100 border-b border-neutral-200 min-w-[110px]',
    },
  },
  {
    accessorKey: 'device510kNumber',
    header: () => (
      <Text variant="body" color="primary">
        510(k) Number
      </Text>
    ),
    cell: ({ row }) => {
      return (
        <Text variant="body" className="text-neutral-600">
          {row.original.device510kNumber}
        </Text>
      );
    },
    meta: {
      cellClassName: 'bg-white border-b border-neutral-200 min-w-[135px]',
      headerClassName:
        'bg-neutral-100 border-b border-neutral-200 min-w-[135px]',
    },
  },
  {
    accessorKey: 'decisionDate',
    header: () => (
      <Text variant="body" color="primary">
        Decision Date
      </Text>
    ),
    cell: ({ row }) => {
      return (
        <Text variant="body" className="text-neutral-600">
          {row.original.decisionDate
            ? new Date(row.original.decisionDate).toLocaleDateString()
            : ''}
        </Text>
      );
    },
    meta: {
      cellClassName: 'bg-white border-b border-neutral-200 min-w-[125px]',
      headerClassName:
        'bg-neutral-100 border-b border-neutral-200 min-w-[125px]',
    },
  },
];

const AdditionalDetails: FC<{
  submission: NonNullable<
    Fda510kSubmissionDbSearchToolOutput['submissions']
  >[number];
}> = ({ submission }) => {
  const regulationLink = generateRegulationLink(
    submission.regulationNumber ?? null
  );
  return (
    <FlexCol gap="2">
      <Box className="grid grid-cols-2 gap-2">
        {submission.regulationNumber && (
          <FlexRow gap="1">
            <Text variant="body-sm" className="text-neutral-600">
              · Regulation Number:
            </Text>
            {regulationLink ? (
              <a
                href={regulationLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-neutral-600 underline font-mono"
              >
                {submission.regulationNumber}
              </a>
            ) : (
              <Text variant="body-sm" className="text-neutral-600">
                {submission.regulationNumber}
              </Text>
            )}
          </FlexRow>
        )}
        {submission.decision && (
          <FlexRow gap="1">
            <Text variant="body-sm" className="text-neutral-600">
              · Decision:
            </Text>
            <Text variant="body-sm" className="text-neutral-600">
              {submission.decision}
            </Text>
          </FlexRow>
        )}
        {submission.dateReceived && (
          <FlexRow gap="1">
            <Text variant="body-sm" className="text-neutral-600">
              · Date Received:
            </Text>
            <Text variant="body-sm" className="text-neutral-600">
              {submission.dateReceived
                ? new Date(submission.dateReceived).toLocaleDateString()
                : ''}
            </Text>
          </FlexRow>
        )}
        {submission.productCode && (
          <FlexRow gap="1">
            <Text variant="body-sm" className="text-neutral-600">
              · Classification Product Code:
            </Text>
            <Text variant="body-sm" className="text-neutral-600">
              {submission.productCode}
            </Text>
          </FlexRow>
        )}
      </Box>
    </FlexCol>
  );
};
